import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { toast } from "sonner";
import { Gamepad2, Check, Download, Copy, ArrowLeft, Loader2, ShieldCheck, Lock } from "lucide-react";

const FEATURES = [
  "Adds the VStars App to your server's phone (NPWD, qb-phone, lb-phone, Road, legacy iframe)",
  "Full VirtuStars experience in-game: feed, music, TV, messages & crews",
  "One-time purchase — your own unique activation code",
  "Locks to one server so the code can't be shared or resold",
];

const FIVEM_VIDEO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_avatar-entertainment-1/artifacts/1a53a2ef00081515_7a8276736e7f4840a7b7ae880288beb0.mov";

export default function FiveMStore() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [licenses, setLicenses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [price, setPrice] = useState(19.99);

  useEffect(() => { api.get("/meta").then(({ data }) => { if (data?.fivem_price != null) setPrice(data.fivem_price); }).catch(() => {}); }, []);

  const loadLicenses = () => { if (user) api.get("/fivem/licenses").then(({ data }) => setLicenses(data)).catch(() => {}); };
  useEffect(() => { loadLicenses(); }, [user]); // eslint-disable-line

  // Returning from Stripe checkout
  useEffect(() => {
    const sid = sp.get("session_id");
    if (sid && user) {
      setChecking(true);
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/fivem/status/${sid}`);
          if (data.payment_status === "paid") { toast.success("Purchase complete! Your license is ready."); loadLicenses(); setChecking(false); return; }
        } catch {}
        if (tries < 8) setTimeout(poll, 1500); else { setChecking(false); }
      };
      poll();
    }
    if (sp.get("canceled")) toast("Checkout canceled");
  }, [sp, user]); // eslint-disable-line

  const buy = async () => {
    if (!user) { toast("Sign in first to purchase"); nav("/login"); return; }
    setBusy(true);
    try { const { data } = await api.post("/fivem/checkout", { origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errText(e)); setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  const download = async (code) => {
    try {
      const res = await api.get(`/fivem/download?code=${encodeURIComponent(code)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "VirtuStars-FiveM.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed — purchase required"); }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <header className="sticky top-0 z-40 glass border-b border-white/8"><div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between"><Logo /><Link to={user ? "/home" : "/login"} className="text-sm text-muted-foreground hover:text-white flex items-center gap-1"><ArrowLeft size={15} /> {user ? "App" : "Sign in"}</Link></div></header>
      <main className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        <div className="relative overflow-hidden rounded-3xl surface glass-gold p-6 md:p-8">
          <div className="flex items-center gap-2 text-gold text-xs font-semibold tracking-widest uppercase mb-2"><Gamepad2 size={14} /> FiveM Server Script</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl">Put <span className="text-gold-grad">VStars</span> on your server's phone</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">Give every player the VirtuStars app inside their in-game phone. One-time purchase, instant download, and a unique activation code locked to your server.</p>
          <div className="mt-4 flex items-end gap-3">
            <div className="font-display font-extrabold text-4xl" data-testid="fivem-price">${price.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground pb-1.5">one-time · one server</div>
          </div>
          <button onClick={buy} disabled={busy} className="btn-gold rounded-full px-6 py-3 text-sm font-semibold mt-4 inline-flex items-center gap-2" data-testid="fivem-buy-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Buy the FiveM Script
          </button>
        </div>

        <div className="surface rounded-2xl overflow-hidden" data-testid="fivem-video-card">
          <div className="px-5 pt-5 pb-3">
            <h2 className="font-display font-semibold flex items-center gap-2"><Gamepad2 size={16} className="text-gold" /> See it in action</h2>
            <p className="text-xs text-muted-foreground mt-1">Watch the VStars app running live inside a FiveM server phone.</p>
          </div>
          <video src={FIVEM_VIDEO} controls playsInline preload="metadata" className="w-full bg-black aspect-video" data-testid="fivem-video" />
        </div>

        <div className="surface rounded-2xl p-5">
          <h2 className="font-display font-semibold mb-3">What you get</h2>
          <ul className="space-y-2">{FEATURES.map((f, i) => <li key={i} className="flex gap-2 text-sm text-silver"><Check size={16} className="text-gold shrink-0 mt-0.5" />{f}</li>)}</ul>
        </div>

        {checking && <div className="surface rounded-2xl p-5 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={16} className="animate-spin" /> Confirming your payment…</div>}

        {licenses.length > 0 && (
          <div className="surface rounded-2xl p-5" data-testid="fivem-licenses">
            <h2 className="font-display font-semibold mb-3 flex items-center gap-2"><ShieldCheck size={16} className="text-emerald-400" /> Your licenses</h2>
            <div className="space-y-3">{licenses.map((l) => (
              <div key={l.id} className="surface-2 rounded-xl p-4" data-testid={`fivem-license-${l.code}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Activation code</div>
                    <div className="font-mono text-lg text-gold flex items-center gap-2">{l.code}<button onClick={() => copy(l.code)} className="text-eblue" data-testid={`fivem-copy-${l.code}`}><Copy size={14} /></button></div>
                  </div>
                  <button onClick={() => download(l.code)} className="btn-blue rounded-full px-4 py-2 text-sm inline-flex items-center gap-1.5" data-testid={`fivem-download-${l.code}`}><Download size={15} /> Download ZIP</button>
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">{l.server_id ? `Activated on server: ${l.server_id}` : "Not yet activated — locks to the first server that uses it."}</div>
              </div>
            ))}</div>
          </div>
        )}

        <div className="surface rounded-2xl p-5">
          <h2 className="font-display font-semibold mb-2">How to install</h2>
          <ol className="text-sm text-silver space-y-2 list-decimal pl-5">
            <li>Extract the ZIP you downloaded above.</li>
            <li>Open <b>README.md</b> and follow the setup for your phone resource.</li>
            <li>Select your phone in <b>config.lua</b>.</li>
            <li>Add your <b>activation code</b> in the config and start the resource — it locks to your server on first launch.</li>
          </ol>
          <p className="text-[11px] text-muted-foreground mt-3">Your code is tied to one server and your VirtuStars account, so it can't be passed around.</p>
        </div>
      </main>
    </div>
  );
}
